<script setup lang="ts">
import {computed, defineModel, onMounted, onUnmounted, watch} from "vue";
import OLMap from "ol/Map";
import View from "ol/View";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import GeoJSON from "ol/format/GeoJSON";
import {defaults as defaultControls} from "ol/control";
import {storeToRefs} from "pinia";
import {useFilesStore} from "@/stores/files";
import {useStore} from "@/stores";
import "ol/ol.css";
import IconMapZoomIn from "@/assets/icons/IconMapZoomIn.vue";
import IconMapZoomOut from "@/assets/icons/IconMapZoomOut.vue";
import GlobalLoader from "@/components/ui/loaders/GlobalLoader.vue";
import type {MapDisplayParamsKey} from "@/composables/constants/params";
import {VIEW_TYPE_IDS} from "@/composables/constants/map";
import {ForecastParamData} from "@/types/files";
import type {DistrictKey} from "@/composables/functions/getDistrictName";
import type {RegionKey} from "@/composables/functions/getRegionName";

import { zoomIn, zoomOut, animateToInitialView, animateToFeature } from "./utils/mapControl";

// Импорт констант.
import {INITIAL_CENTER, INITIAL_ZOOM, MAP_EXTENT} from "./utils/mapConstants";
import {MapClickHandlers} from "@/components/modules/MainMap/composables/mapHandlers";
import {createDistrictStyleFunction, createRegionStyleFunction} from "@/components/modules/MainMap/utils/mapStyles";

const selectedDistrict = defineModel<DistrictKey | null>("selectedDistrict", {required: true});
const selectedRegion = defineModel<RegionKey | null>("selectedRegion", {required: true});
// Флаг загрузки данных
const isDataLoading = defineModel<boolean>("isDataLoading", {required: true});
// Флаг загрузки карты
const isMapLoading = defineModel<boolean>("isMapLoading", {required: true});


interface Props {
  selectedParams: MapDisplayParamsKey;
}

const props = defineProps<Props>();

const filesStore = useFilesStore();
const {filesData, currentFileDistrictsData} = storeToRefs(filesStore);

const mainStore = useStore();
const {forecastStatusDisplay, currentForecast} = storeToRefs(mainStore);

// Убираем ПКМ с карты, что бы не было возможности сохранения изображения.
const handleMapContextMenu = (event: MouseEvent) => {
  event.preventDefault();
};

// Экземпляр карты.
let map: OLMap | null = null;

// Слой карты с регионами
let vectorLayer: VectorLayer<VectorSource> | null = null;
// Слой карты с федеральными округами
let vectorLayerDistrict: VectorLayer<VectorSource> | null = null;

// Данные по регионам в зависимости от прогноза.
const regionDataMap = computed(() => {
  const dataMap = new Map();

  if (forecastStatusDisplay.value && currentForecast.value) {
    for (const [region, regionParams] of Object.entries(currentForecast.value.regions)) {
      dataMap.set(region.toLowerCase(), regionParams);
    }
    return dataMap;
  }

  if (!filesData.value || !filesData.value.results) {
    return new Map();
  }

  for (const fileData of filesData.value.results) {
    dataMap.set(fileData.region.toLowerCase(), fileData);
  }

  return dataMap;
});

// Данные по федеральным округам в зависимости от прогноза.
const districtDataMap = computed(() => {
  const dataMap = new Map();

  // Если данных нет, возвращаем пустую Map
  if (!currentFileDistrictsData.value) {
    return dataMap;
  }

  // Если активен режим прогноза
  if (forecastStatusDisplay.value && currentForecast.value) {
    for (const [district, districtParams] of Object.entries(currentForecast.value.federal_districts)) {
      const paramData = districtParams[props.selectedParams] as ForecastParamData | undefined;

      // Проверяем, что данные для выбранного параметра существуют
      if (paramData && paramData.prediction_value && paramData.percent_to_previous_year) {
        dataMap.set(district.toLowerCase(), {
          [props.selectedParams]: {
            [VIEW_TYPE_IDS.ABSOLUTE]: paramData.prediction_value,
            percent_to_previous_year: paramData.percent_to_previous_year
          }
        });
      }
    }
    return dataMap;
  }

  // Преобразуем данные из currentFileDistrictsData
  for (const [districtKey, districtInfo] of Object.entries(currentFileDistrictsData.value)) {
    // Нормализуем ключ к lowercase для единообразия
    const normalizedKey = districtKey.toLowerCase();

    // Преобразуем формат данных
    dataMap.set(normalizedKey, {
      [props.selectedParams]: {
        [VIEW_TYPE_IDS.ABSOLUTE]: districtInfo.value,
        percent_to_previous_year: districtInfo.percentToPreviousYear
      }
    });
  }

  return dataMap;
});


// Обновление слоев карты.
const updateMapStyle = () => {
  if (vectorLayerDistrict && vectorLayer && map) {
    // Пересоздаем функции стиля с актуальными параметрами
    const districtStyleFn = createDistrictStyleFunction(districtDataMap, props.selectedParams);
    const regionStyleFn = createRegionStyleFunction(regionDataMap, props.selectedParams, forecastStatusDisplay, map);

    // Устанавливаем новые функции стиля
    vectorLayerDistrict.setStyle(districtStyleFn);
    vectorLayer.setStyle(regionStyleFn);

    // Обновляем слои
    vectorLayer.changed();
    vectorLayerDistrict.changed();
  }
};

// Инициализация карты.
onMounted(async () => {
  // isMapLoading.value = true;

  // Загружаем GeoJSON файлы
  const [russiaRegionsGeoJSON, russiaDistrictsGeoJSON] = await Promise.all([
    fetch(new URL("@/composables/geojson/russia_regions.geojson", import.meta.url).href).then(r => r.json()),
    fetch(new URL("@/composables/geojson/russia_districts.geojson", import.meta.url).href).then(r => r.json())
  ]);

  // Векторные источники
  const vectorDistrictSource = new VectorSource({
    features: new GeoJSON().readFeatures(russiaDistrictsGeoJSON, {
      featureProjection: "EPSG:3857"
    })
  });

  const vectorRegionSource = new VectorSource({
    features: new GeoJSON().readFeatures(russiaRegionsGeoJSON, {
      featureProjection: "EPSG:3857"
    })
  });

  // Создание базовых слоев карты без стилей.
  vectorLayerDistrict = new VectorLayer({
    source: vectorDistrictSource
  });

  vectorLayer = new VectorLayer({
    source: vectorRegionSource
  });

  // Начальная видимость слоев в зависимости от выбора округа/региона
  vectorLayerDistrict.setVisible(selectedDistrict.value === null);
  vectorLayer.setVisible(selectedDistrict.value !== null);

  // Инициализация карты.
  map = new OLMap({
    target: "map",
    controls: defaultControls({
      zoom: false,
      rotate: false
    }),
    layers: [
      vectorLayerDistrict,
      vectorLayer
    ],
    view: new View({
      center: INITIAL_CENTER,
      zoom: INITIAL_ZOOM,
      minZoom: 3.2,
      maxZoom: 8,
      extent: MAP_EXTENT,
      constrainOnlyCenter: true
    })
  });

  // Инициализация функций стиля регионов и округов.
  const districtStyleFn = createDistrictStyleFunction(districtDataMap, props.selectedParams);
  const regionStyleFn = createRegionStyleFunction(regionDataMap, props.selectedParams, forecastStatusDisplay, map);

  vectorLayerDistrict.setStyle(districtStyleFn);
  vectorLayer.setStyle(regionStyleFn);

  // Инициализация обработчиков клика/наведения
  MapClickHandlers({
    map,
    districtDataMap,
    regionDataMap,
    selectedDistrict,
    selectedRegion
  });

  // isMapLoading.value = false;
});

onUnmounted(() => {
  if (map) {
    map.setTarget(undefined);
    map = null;
  }
});

// ==================== WATCHERS ====================

// Обновление карты после изменения режима прогноза.
watch(
    () => [forecastStatusDisplay.value, currentForecast.value],
    ([newForecastStatus, newCurrentForecast]) => {
      console.log("Режим прогноза изменился:", {
        forecastStatusDisplay: newForecastStatus,
        hasForecastData: !!newCurrentForecast
      });
      updateMapStyle();
    },
    { deep: true }
);

// Обновление карты после выбора нового округа
watch(
    () => selectedDistrict.value,
    (newDistrict) => {
      if (vectorLayerDistrict && vectorLayer) {
        vectorLayerDistrict.setVisible(newDistrict === null);
        vectorLayer.setVisible(newDistrict !== null);

        console.log(`Переключение слоя: ${newDistrict === null ? "округа" : "регионы"}`);

        if (newDistrict === null) {
          animateToInitialView(map);
        } else if (!selectedRegion.value) {
          animateToFeature(map, newDistrict, true, vectorLayerDistrict, vectorLayer);
        }
      }
    }
);

// Обновление карты после выбора нового региона
watch(
    () => selectedRegion.value,
    (newRegion) => {
      if (newRegion) {
        if (vectorLayerDistrict && vectorLayer) {
          vectorLayerDistrict.setVisible(false);
          vectorLayer.setVisible(true);
        }
        animateToFeature(map, newRegion, false, vectorLayerDistrict, vectorLayer);
      }
    }
);

// Обновление карты после изменения данных
watch(
    () => isDataLoading.value,
    (loading) => {
      // Обновляем стили только ПОСЛЕ загрузки данных
      if (!loading) {

        console.log("Данные загружены, обновляем карту");
        updateMapStyle();
      }
    }
);

// Обновление карты после изменения выбранного параметра
watch(
    () => props.selectedParams,
    (newParams) => {
      console.log("selectedParams изменился на:", newParams);
      updateMapStyle();
    }
);

// Обновление карты при изменении данных файлов
watch(
    () => filesData.value,
    (newData) => {
      if (newData && newData.results) {
        console.log("filesData изменились, обновляем карту. Кол-во регионов:", newData.results.length);
        updateMapStyle();
      }
    }
);

// Обновление карты при изменении данных округов
watch(
    () => currentFileDistrictsData.value,
    (newData) => {
      if (newData) {
        console.log("currentFileDistrictsData изменились, обновляем карту");
        updateMapStyle();
      }
    }
);
</script>

<template>
  <div class="main-map">
    <div
      id="map"
      @contextmenu="handleMapContextMenu"
    />
    <div class="zoom">
      <div
        class="zoom__item"
        @click="zoomIn(map)"
      >
        <IconMapZoomIn />
      </div>
      <div
        class="zoom__item"
        @click="zoomOut(map)"
      >
        <IconMapZoomOut />
      </div>
    </div>
    <GlobalLoader
      v-if="isMapLoading"
      position="absolute"
      backdrop-filter="4"
    />
  </div>
</template>

<style scoped lang="scss">
.main-map {
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  position: fixed;
  z-index: 2;

  #map {
    width: 100%;
    height: 100%;
  }
}

.zoom {
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;

  &__item {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    background-color: var(--background-bg-white);
    box-shadow: 1px 2px 10px 0 var(--gray-gray50);
    cursor: pointer;
    user-select: none;
  }
}
</style>